import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Navbar } from '../components/Navbar';
import { ArticleCard } from '../components/ArticleCard';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import { TrendingUp, Clock, Sparkles, Search as SearchIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

import { API, authFetch } from '../lib/api';

const TOPICS = [
  'Technology', 'AI', 'Programming', 'Business', 'Personal Development',
  'Design', 'Science', 'Health', 'Productivity', 'Startups'
];

export const HomePage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [popularTags, setPopularTags] = useState([]);
  const [trendingSearches, setTrendingSearches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedType, setFeedType] = useState(searchParams.get('feed') || 'latest');

  useEffect(() => {
    fetchArticles();
    fetchPopularTags();
    fetchTrendingSearches();
  }, [feedType, user]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let endpoint = `${API}/articles?feed_type=${feedType}&limit=20`;
      if (feedType === 'following' && user) {
        endpoint = `${API}/articles/feed?limit=20`;
      }
      
      const response = await authFetch(endpoint);
      
      if (response.ok) {
        const data = await response.json();
        setArticles(data);
      }
    } catch (error) {
      console.error('Failed to fetch articles:', error);
    }
    setLoading(false);
  };

  const fetchPopularTags = async () => {
    try {
      const response = await authFetch(`${API}/tags?limit=10`);
      if (response.ok) setPopularTags(await response.json());
    } catch (error) {
      console.error('Failed to fetch tags:', error);
    }
  };

  const fetchTrendingSearches = async () => {
    try {
      const response = await authFetch(`${API}/search/trending?limit=6`);
      if (response.ok) setTrendingSearches(await response.json());
    } catch (e) {
      console.error('Failed to fetch trending searches:', e);
    }
  };

  const handleFeedChange = (value) => {
    setFeedType(value);
    setSearchParams({ feed: value });
  };

  const handleBookmarkChange = (articleId, isBookmarked) => {
    setArticles(prev => prev.map(article => 
      article.article_id === articleId 
        ? { ...article, is_bookmarked: isBookmarked }
        : article
    ));
  };

  return (
    <div className="min-h-screen bg-background paper-texture" data-testid="home-page">
      <Helmet>
        <title>The Blogs Grid — Read, Write, Deepen Your Understanding</title>
        <meta name="description" content="The Blogs Grid is a place to read, write, and deepen your understanding. Discover stories, thinking, and expertise from writers on any topic." />
        <link rel="canonical" href="https://theblogsgrid.com/" />
        <meta property="og:title" content="The Blogs Grid" />
        <meta property="og:description" content="A place to read, write, and deepen your understanding." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://theblogsgrid.com/" />
        <meta property="og:site_name" content="The Blogs Grid" />
        <meta property="og:image" content="https://theblogsgrid.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Blogs Grid" />
        <meta name="twitter:description" content="A place to read, write, and deepen your understanding." />
        <meta name="twitter:image" content="https://theblogsgrid.com/og-image.png" />
        <link rel="alternate" type="application/rss+xml" title="The Blogs Grid — Latest articles" href="/api/feed.xml" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'The Blogs Grid',
          url: 'https://theblogsgrid.com',
          description: 'A modern publishing platform to read, write, and deepen your understanding.',
          potentialAction: {
            '@type': 'SearchAction',
            target: 'https://theblogsgrid.com/search?q={search_term_string}',
            'query-input': 'required name=search_term_string',
          },
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'The Blogs Grid',
          url: 'https://theblogsgrid.com',
          logo: 'https://theblogsgrid.com/favicon.ico',
        })}</script>
      </Helmet>
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8">
            {/* Feed Tabs */}
            <Tabs value={feedType} onValueChange={handleFeedChange} className="mb-8">
              <TabsList className="bg-transparent border-b border-border/50 rounded-none w-full justify-start gap-8 h-auto pb-0">
                {user && (
                  <TabsTrigger 
                    value="following" 
                    className="rounded-none border-b-2 border-transparent data-[state=active]:border-accent data-[state=active]:text-foreground data-[state=active]:bg-transparent pb-3 px-0 text-muted-foreground transition-colors duration-200"
                    data-testid="feed-following"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Following
                  </TabsTrigger>
                )}
                <TabsTrigger 
                  value="latest" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-accent data-[state=active]:text-foreground data-[state=active]:bg-transparent pb-3 px-0 text-muted-foreground transition-colors duration-200"
                  data-testid="feed-latest"
                >
                  <Clock className="w-4 h-4 mr-2" />
                  Latest
                </TabsTrigger>
                <TabsTrigger 
                  value="trending" 
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-accent data-[state=active]:text-foreground data-[state=active]:bg-transparent pb-3 px-0 text-muted-foreground transition-colors duration-200"
                  data-testid="feed-trending"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Trending
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Articles List */}
            <div className="divide-y divide-border stagger-children">
              {loading ? (
                Array(5).fill(0).map((_, i) => (
                  <div key={i} className="py-6">
                    <div className="flex gap-6">
                      <div className="flex-1">
                        <Skeleton className="h-4 w-32 mb-3" />
                        <Skeleton className="h-6 w-full mb-2" />
                        <Skeleton className="h-4 w-3/4 mb-3" />
                        <Skeleton className="h-4 w-48" />
                      </div>
                      <Skeleton className="hidden sm:block w-32 h-32 rounded-lg" />
                    </div>
                  </div>
                ))
              ) : articles.length > 0 ? (
                articles.map((article, index) => (
                  <div 
                    key={article.article_id}
                    className="fade-up"
                    style={{ animationDelay: `${Math.min(index * 0.06, 0.5)}s` }}
                  >
                    <ArticleCard 
                      article={article}
                      onBookmarkChange={handleBookmarkChange}
                    />
                  </div>
                ))
              ) : (
                <div className="py-16 text-center">
                  <p className="text-muted-foreground">
                    {feedType === 'following' 
                      ? "Follow some authors to see their articles here"
                      : "No articles found. Be the first to write one!"}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4">
            <div className="sticky top-20 space-y-8 slide-in-right">
              {/* Topics */}
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
                  Discover Topics
                </h3>
                <div className="flex flex-wrap gap-2">
                  {TOPICS.map(topic => (
                    <Link key={topic} to={`/topic/${topic.toLowerCase()}`}>
                      <Badge 
                        variant="secondary" 
                        className="rounded-full hover:bg-accent hover:text-accent-foreground cursor-pointer transition-all duration-200 press-scale"
                        data-testid={`topic-${topic.toLowerCase()}`}
                      >
                        {topic}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Popular Tags */}
              {popularTags.length > 0 && (
                <div>
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
                    Trending Tags
                  </h3>
                  <div className="space-y-1">
                    {popularTags.slice(0, 8).map(tag => (
                      <Link 
                        key={tag.name} 
                        to={`/topic/${tag.name}`}
                        className="flex items-center justify-between py-2 px-2 -mx-2 rounded-md hover:bg-muted/50 hover:text-accent transition-all duration-200"
                        data-testid={`trending-tag-${tag.name}`}
                      >
                        <span className="text-sm font-medium">#{tag.name}</span>
                        <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{tag.count}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Trending Searches */}
              {trendingSearches.length > 0 && (
                <div data-testid="trending-searches">
                  <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-widest mb-4">
                    Trending Searches
                  </h3>
                  <div className="space-y-1">
                    {trendingSearches.map((item, i) => (
                      <Link
                        key={item.term}
                        to={`/search?q=${encodeURIComponent(item.term)}`}
                        className="flex items-center gap-2.5 py-2 px-2 -mx-2 rounded-md hover:bg-muted/50 hover:text-accent transition-all duration-200 group"
                        data-testid={`trending-search-${i}`}
                      >
                        <SearchIcon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-accent shrink-0" />
                        <span className="text-sm font-medium truncate">{item.term}</span>
                        <span className="ml-auto text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full shrink-0">
                          {item.count}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="pt-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground leading-relaxed">
                  The Blogs Grid — A place to read, write, and deepen your understanding.
                </p>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default HomePage;
